import { useMemo, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import type { GridColDef } from '@mui/x-data-grid';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  claimStatusTone,
  formatCivilDate,
  formatCurrency,
  isApiError,
  links,
  type ClaimApi,
} from '@eis/core';
import { InfiniteDataGrid, RequirePermission, StatusChip, cardSurfaceSx } from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';
import { useInfiniteList } from '../api/policies';

/** One row of a "Schäden" tab — the claim as the policy pages show it (ui-spec §7). */
export interface PolicyClaimRow {
  readonly id: string;
  readonly code: string;
  readonly number: string;
  readonly policyCode: string;
  readonly objectCode: string;
  readonly damageDate: string;
  readonly notificationDate: string;
  readonly description: string;
  readonly status: string;
  readonly amount: number | undefined;
}

/** claimservice's claim as a tab row: civil dates, the number (or the code), the description or title. */
export function toPolicyClaimRow(claim: ClaimApi.ClaimClass): PolicyClaimRow {
  return {
    id: claim.code,
    code: claim.code,
    number: claim.claimNumber === '' ? claim.code : claim.claimNumber,
    policyCode: claim.policyCode,
    objectCode: claim.policyObjectCode,
    damageDate: claim.damageDate.slice(0, 10),
    notificationDate: claim.notificationDate.slice(0, 10),
    description: claim.description === '' ? claim.title : claim.description,
    status: claim.status,
    // claimservice answers `null` for a claim nobody has costed yet, although its OpenAPI types
    // the field `number` (observed 2026-10-04) — never arithmetic on it.
    amount:
      typeof claim.initialCost === 'number' && Number.isFinite(claim.initialCost)
        ? claim.initialCost
        : undefined,
  };
}

/** A claimservice amount (decimal euros) through core formatCurrency; none or 0 is "not submitted" (v1). */
export function claimAmountText(
  amount: number | undefined,
  locale: string,
  notSubmitted: string,
): string {
  return amount === undefined || amount === 0
    ? notSubmitted
    : formatCurrency(Math.round(amount * 100), 'EUR', locale);
}

/** claimservice down (5xx, no answer) — an outage, never "no claims". */
export function isClaimServiceDown(error: unknown): boolean {
  return !isApiError(error) || error.status >= 500;
}

/** The columns both "Schäden" tabs share (ui-spec §7: v1's policy claims tab). */
export function useClaimColumns(locale: string): GridColDef<PolicyClaimRow>[] {
  const { t } = useTranslation('insurance');
  const navigate = useNavigate();
  return useMemo<GridColDef<PolicyClaimRow>[]>(
    () => [
      {
        field: 'number',
        headerName: t('claims.columns.number'),
        width: 160,
        sortable: false,
        renderCell: ({ row }) => (
          <Link
            href={links.claims.claim(row.code)}
            sx={{ fontFamily: 'monospace' }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              void navigate({ href: links.claims.claim(row.code) });
            }}
          >
            {row.number}
          </Link>
        ),
      },
      {
        field: 'damageDate',
        headerName: t('claims.columns.damageDate'),
        width: 120,
        valueFormatter: (value: string) => (value === '' ? '—' : formatCivilDate(value, locale)),
      },
      {
        field: 'notificationDate',
        headerName: t('claims.columns.notificationDate'),
        width: 120,
        valueFormatter: (value: string) => (value === '' ? '—' : formatCivilDate(value, locale)),
      },
      {
        field: 'description',
        headerName: t('claims.columns.description'),
        flex: 1,
        minWidth: 200,
        sortable: false,
      },
      {
        field: 'status',
        headerName: t('claims.columns.status'),
        width: 140,
        renderCell: ({ row }) => (
          <StatusChip
            status={row.status}
            tone={claimStatusTone(row.status)}
            label={t(`claims.status.${row.status}` as never, { defaultValue: row.status })}
          />
        ),
      },
      {
        field: 'amount',
        headerName: t('claims.columns.amount'),
        width: 140,
        sortable: false,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number | undefined) =>
          claimAmountText(value, locale, t('claims.notSubmitted')),
      },
    ],
    [t, locale, navigate],
  );
}

/** The catalog's sortable keys, by column (CLAIM_FILTER_CATALOG). */
export const CLAIM_SORT_KEYS: Readonly<Record<string, string>> = {
  damageDate: 'damageDate',
  notificationDate: 'notificationDate',
  status: 'status',
};

/**
 * The policy's "Schäden" tab (SDD-018 §9, ui-spec §7; prompt 26 phase 26.7): its claims, read from
 * claimservice through `@eis/core` (`policyCode==<code>`, newest damage first) — never a byte of
 * the claims module's code (backbone §1). A row opens the claim in the claims area; "Schaden
 * melden" opens its create flow with this policy (`links.claims.newClaim`), for whoever holds
 * `claims.create`. No delete: that is the suite's.
 */
export function PolicyClaimsTab({
  code,
  locale,
}: {
  readonly code: string;
  readonly locale: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const navigate = useNavigate();
  const list = useInfiniteList('claims', { filters: `policyCode==${code}`, order: '-damageDate' });
  const rows = useMemo(() => list.items.map(toPolicyClaimRow), [list.items]);
  const columns = useClaimColumns(locale);

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({ p: 2.5, ...cardSurfaceSx(theme) })}
      data-testid="policy-claims"
    >
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{ alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h3" component="h2">
              {t('claims.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('claims.description')}
            </Typography>
          </Box>
          <RequirePermission anyOf={[p.claims.create]}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                void navigate({ href: links.claims.newClaim({ policyCode: code }) });
              }}
            >
              {t('claims.report')}
            </Button>
          </RequirePermission>
        </Stack>
        <InfiniteDataGrid
          rows={rows}
          list={list}
          columns={columns}
          ariaLabel={t('claims.ariaLabel')}
          errorDescription={
            isClaimServiceDown(list.error) ? t('claims.unavailable') : t('claims.error')
          }
          emptyTitle={t('claims.empty.title')}
          emptyDescription={t('claims.empty.description')}
          onRowClick={(row) => {
            void navigate({ href: links.claims.claim(row.code) });
          }}
        />
      </Stack>
    </Paper>
  );
}
